import { __ } from '../Lang.js';

// Configurable threshold: months before due date to show "due soon" warning
export const DUE_SOON_THRESHOLD_MONTHS = 2;

/** Format a YYYY-MM-DD (or ISO datetime) as MM/DD/YYYY; null if unparseable. */
function formatDatePart(dateString) {
  if (!dateString || dateString === '0000-00-00') return null;
  const [y, m, d] = String(dateString).split('T')[0].split(' ')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  // Parse parts explicitly to avoid the UTC shift on YYYY-MM-DD strings.
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/**
 * Calculate renewal status based on renewal date and submission status
 * @param {string} renewalDate - Renewal DUE date, YYYY-MM-DD
 * @param {string} [submissionStatus] - Renewal status (e.g. 'submitted', 'not started')
 * @param {string} [submittedDate] - Date the renewal was actually submitted, YYYY-MM-DD
 * @returns {{ text: string, colorClass: string }}
 */
export function getRenewalStatus(renewalDate, submissionStatus, submittedDate) {
  if (!renewalDate || renewalDate === '0000-00-00') return { text: '', colorClass: '' };

  // Submitted: show the date it was SUBMITTED — never the due date, which is a
  // future date and reads as wrong under a "SUBMITTED" label. If a legacy row has
  // no submitted_date, show the label alone rather than a misleading date.
  if (submissionStatus === 'submitted') {
    const formatted = formatDatePart(submittedDate);
    return {
      text: formatted ? __('RENEWAL SUBMITTED') + ' ' + formatted : __('RENEWAL SUBMITTED'),
      colorClass: 'status-due-later',
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Parse date parts explicitly to avoid timezone issues with YYYY-MM-DD strings
  const [year, month, day] = renewalDate.split('-').map(Number);
  const renewalDt = new Date(year, month - 1, day); // month is 0-indexed

  const dueSoonDate = new Date(today);
  dueSoonDate.setMonth(dueSoonDate.getMonth() + DUE_SOON_THRESHOLD_MONTHS);

  const formattedDate = renewalDt.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });

  if (renewalDt < today) {
    return { text: __('RENEWAL PAST DUE'), colorClass: 'status-past-due' };
  } else if (renewalDt <= dueSoonDate) {
    return { text: __('RENEWAL DUE') + ' ' + formattedDate, colorClass: 'status-due-soon' };
  } else {
    return { text: __('RENEWAL DUE') + ' ' + formattedDate, colorClass: 'status-due-later' };
  }
}
